import { NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, EnvironmentProviders, ErrorHandler, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouteReuseStrategy } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { IonicModule } from '@ionic/angular';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { NgxsWebSocketPluginModule } from '@ngxs/websocket-plugin';
import * as Sentry from '@sentry/angular';
import { API_URL, GpAuthService, GpAuthState, TokenInterceptor } from 'gp-auth';
import { GpAsteriskModule } from 'gp-asterisk';
import { CookieService } from 'ngx-cookie-service';
import { ASYNC_STORAGE, AsyncLocalStorage } from 'rt-async-storage';
import { RtImageEditorComponent } from 'rt-image-editor';
import { factoryFn, RtPlatformModule, WINDOW, WindowService } from 'rt-platform';
import { RtRouterDataResolverState } from 'rt-router-data-resolver';
import { RtSvgSpriteModule } from 'rt-svg-sprite';
import { RtTitlesModule } from 'rt-titles';
import { RtToastModule, RtToastPosition, RtToastService } from 'rt-toasts';
import {
  AccessState,
  AuthGuard,
  CheckVersionService,
  GpsLocationService,
  OrderListState,
  OrdersService,
  ProfileService,
  ProfileState,
  StandListState,
  StandService
} from 'shared';
import { AccessService } from '../../../../shared/src/access/access.service';
import { Settings } from '../conf/settings';
import { environment, settings } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CallCardComponent } from './calls/call-card.component';
import { SwipeDismissDirective } from './calls/swipe-dismiss.directive';
import { CallOrderModalComponent } from './calls/call-order-modal.component';
import { CallDirectorySyncService } from './call-directory/call-directory-sync.service';
import { GpsService } from './gps/gps.service';
import { HeaderComponent } from './header/header/header.component';
import { ApiPrefixInterceptor } from './host.interceptor';
import { nativeWsBase } from './server-urls';
import { SoftphoneCallComponent } from './softphone/softphone-call.component';

const PROVIDERS: (Provider | EnvironmentProviders)[] = [
  { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  { provide: Settings, useValue: settings },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ApiPrefixInterceptor,
    multi: true
  },
  { provide: HTTP_INTERCEPTORS, multi: true, useClass: TokenInterceptor },
  { provide: API_URL, useValue: settings.BASE_URL },
  {
    provide: ASYNC_STORAGE,
    useValue: Preferences
  },
  {
    provide: WINDOW,
    useFactory: factoryFn,
    deps: [WindowService]
  },
  AuthGuard,
  GpAuthService,
  CookieService,
  Settings,
  WindowService,
  RtToastService,
  {
    provide: ASYNC_STORAGE,
    useClass: AsyncLocalStorage
  },
  provideIonicAngular(),
  provideHttpClient(withFetch(), withInterceptorsFromDi()),
  ProfileService,
  AccessService,
  GpsService,
  GpsLocationService,
  CheckVersionService,
  OrdersService,
  StandService,
  CallDirectorySyncService,
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler()
  },
  {
    provide: Sentry.TraceService,
    deps: [Router]
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => {
    },
    deps: [Sentry.TraceService],
    multi: true
  }
];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    SoftphoneCallComponent,
    BrowserModule,
    BrowserAnimationsModule,
    NgxsWebSocketPluginModule.forRoot({ reconnectInterval: 2000 }),
    NgxsModule.forRoot([GpAuthState, ProfileState, AccessState, RtRouterDataResolverState, OrderListState, StandListState], {
      developmentMode: !environment.production
    }),
    NgxsRouterPluginModule.forRoot(),
    // NgxsAsyncStoragePluginModule.forRoot(CapacitorStorageEngine, {
    //   key: [GpAuthState]
    // }),
    NgxsStoragePluginModule.forRoot({ keys: [GpAuthState] }),
    GpAsteriskModule.forRoot({ wsPort: 3330, appName: 'VaillantApp', wsBaseUrl: nativeWsBase() }),

    // NgxsLoggerPluginModule.forRoot(),
    RtPlatformModule,
    IonicModule,
    NgOptimizedImage,

    RtToastModule.forRoot({
      position: RtToastPosition.TopRight,
      isShowTimeout: true,
      groupByDuplicate: true
    }),
    AppRoutingModule,
    HeaderComponent,
    RtTitlesModule.forRoot({ titleDefault: 'Vaillant service', titleSeparator: ' - ' }),
    RtSvgSpriteModule,
    RtImageEditorComponent,
    CallCardComponent,
    SwipeDismissDirective,
    CallOrderModalComponent
  ],
  providers: [PROVIDERS]
})
export class AppModule {
}
